import { fetcher, IGlobalRequestOptions } from "common/api/Fetcher";
import { IEstimateOverviewData } from "common/records/EstimateOverview";
import { IEstimateData } from "common/records/Estimate";
import { IEstimateCommissionData } from "common/records/EstimateCommission";
import { PatternTestDataRecord } from "common/records/PatternTestData";
import { EstimateParamsRecord } from "common/records/index/EstimateParams";
import { toParams } from "common/records/index/QueryParamsBase";
import { IMetaPaginationData } from "common/records/MetaPagination";
import { IdType } from "common/types";
import { IEstimateEmailParamsData, IEstimateEmailParamsRecord } from "common/records/index/EstimateEmailParams";
import { estimateIncludes } from "common/api/Api";
import { downloadFile, downloadFileName } from "common/helpers/File";
import { Map } from "immutable";

const jobUrl = "api/v1/jobs/:id/estimates";
export const estimateUrl = "/api/v1/estimates";

export interface IEstimatePayload {
  estimate: Record<string, unknown> & {
    groups_attributes?: Record<string, unknown>[];
    estimate_finance_options_attributes?: Record<string, unknown>[];
  };
}

export interface IEstimateIndexResponse {
  estimates: IEstimateOverviewData[];
  meta: { pagination: IMetaPaginationData };
}
export interface IEstimateResponse {
  estimate: IEstimateData;
}

export interface PatternParams {
  doc_type: string;
  job_id: number;
}

export type EstimateListingType = "estimates" | "proposals" | "contracts";

export const list = (
  queryParams: EstimateParamsRecord,
  options?: IGlobalRequestOptions,
): Promise<IEstimateIndexResponse> => {
  const parameters = toParams(queryParams);

  const actionUrl = jobUrl.replace(":id", parameters["job_id"].toString());
  return options ? fetcher.get(actionUrl, parameters, options) : fetcher.get(actionUrl, parameters);
};

export const load = (
  jobId: number,
  estimateId: number,
  options?: IGlobalRequestOptions,
): Promise<IEstimateResponse> => {
  const actionUrl = fetcher.joinUrls(jobUrl.replace(":id", jobId.toString()), estimateId.toString());
  return options
    ? fetcher.get(actionUrl, { "include[]": estimateIncludes }, options)
    : fetcher.get(actionUrl, { "include[]": estimateIncludes });
};

export const destroy = (jobId: number, estimateId: number): Promise<void> => {
  const actionUrl = fetcher.joinUrls(jobUrl.replace(":id", jobId.toString()), estimateId.toString());
  return fetcher.delete<void>(actionUrl);
};

export const create = (jobId: number, payload: IEstimatePayload): Promise<IEstimateResponse> => {
  const actionUrl = jobUrl.replace(":id", jobId.toString());
  return fetcher.post(actionUrl, {
    ...payload,
    include: estimateIncludes,
  });
};

export const update = (jobId: number, estimateId: number, payload: IEstimatePayload): Promise<IEstimateResponse> => {
  const actionUrl = fetcher.joinUrls(jobUrl.replace(":id", jobId.toString()), estimateId.toString());
  return fetcher.patch(actionUrl, {
    ...payload,
    include: estimateIncludes,
  });
};

export const pattern = async (patternTestDataRecord: PatternTestDataRecord) => {
  const { job_id, estimate_id, doc_type } = patternTestDataRecord;
  const actionUrl = fetcher.joinUrls(estimateUrl, `${estimate_id?.toString()}.pattern`);
  const response = await fetcher.get(actionUrl, { job_id, doc_type });
  return JSON.parse(response as string);
};

export const emailEstimate = (
  estimateId: IdType,
  params: IEstimateEmailParamsRecord,
): Promise<IEstimateEmailParamsData> => {
  const actionUrl = fetcher.joinUrls(estimateUrl, `${estimateId.toString()}/email`);
  return fetcher.post<IEstimateEmailParamsData>(actionUrl, params);
};

export const attachIntegration = (estimateId: IdType, documentType: string): Promise<void> => {
  const actionUrl = fetcher.joinUrls(estimateUrl, `${estimateId.toString()}/ms_upload`);
  return fetcher.post(actionUrl, { doc_type: documentType });
};

export const getEstimatePdf = (
  estimateId: IdType,
  documentType: "estimate" | "contract" | "cost" | "commission" | "inspection",
  jobName: string,
  options?: { inspection_include_all_items?: boolean },
): Promise<void> => {
  let actionUrl =
    documentType === "commission"
      ? fetcher.joinUrls(estimateUrl, estimateId.toString(), "commission.pdf")
      : fetcher.joinUrls(estimateUrl, `${estimateId.toString()}.pdf?doc_type=${documentType}`);
  if (documentType === "inspection" && options?.inspection_include_all_items !== undefined) {
    actionUrl += `&options[inspection_include_all_items]=${options.inspection_include_all_items}`;
  }
  return downloadFile(actionUrl, downloadFileName(`${jobName} - ${estimateId}.pdf`, true));
};

export const getEstimateCsv = (
  estimateId: IdType,
  documentType: "product" | "remeasure",
  jobName: string,
): Promise<void> => {
  const actionUrl = fetcher.joinUrls(estimateUrl, `${estimateId.toString()}.csv?doc_type=${documentType}`);
  return downloadFile(actionUrl, downloadFileName(`${jobName} - ${estimateId}.csv`, true));
};

export const validateCommission = (estimateId: IdType, custom_config: Map<string, unknown>): Promise<void> => {
  const actionUrl = fetcher.joinUrls(estimateUrl, `${estimateId.toString()}/validate_commission`);
  return fetcher.post(actionUrl, {
    commission_config: { custom_config: custom_config.toJS() },
    commission_method: "custom",
  });
};

export const getEstimateCommission = (estimateId: IdType): Promise<IEstimateCommissionData> => {
  const actionUrl = fetcher.joinUrls(estimateUrl, estimateId.toString(), "commission");
  return fetcher.get<IEstimateCommissionData>(actionUrl);
};
